// src/admin/UserManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { userAPI } from '../services/api';
import { getStatusInfo, formatDateTime, copyToClipboard } from '../utils/helpers';
import UserFormModal from '../components/UserFormModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import StatusUpdateModal from '../components/StatusUpdateModal';
// import DeleteConfirmModal from './components/DeleteConfirmModal';
import Toast from '../components/Toast';
import './UserManagement.css';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Modal states
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [statusUpdateUser, setStatusUpdateUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  
  // Toast state
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      setUsers(response.data || response);
    } catch (err) {
      showToast(err.message || 'Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleUserFormSubmit = async (userData) => {
    try {
      if (editingUser) {
        const response = await userAPI.update(editingUser.id, userData);
        setUsers(users.map(user => 
          user.id === editingUser.id ? response.data : user
        ));
        showToast('User updated successfully', 'success');
      } else {
        const response = await userAPI.create(userData);
        setUsers([response.data, ...users]);
        showToast('User created successfully', 'success');
      }
      setShowUserForm(false);
      setEditingUser(null);
    } catch (err) {
      throw err;
    }
  };

  const handleResetPassword = async (password) => {
    try {
      await userAPI.resetPassword(resetPasswordUser.id, password);
      setResetPasswordUser(null);
      showToast('Password reset successfully', 'success');
    } catch (err) {
      throw err;
    }
  };

  const handleStatusUpdate = async (status) => {
    try {
      const response = await userAPI.updateStatus(statusUpdateUser.id, status);
      setUsers(users.map(user => 
        user.id === statusUpdateUser.id ? response.data : user
      ));
      setStatusUpdateUser(null);
      showToast('User status updated successfully', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'error');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await userAPI.delete(deleteConfirmUser.id);
      setUsers(users.filter(user => user.id !== deleteConfirmUser.id));
      setDeleteConfirmUser(null);
      showToast('User deleted successfully', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete user', 'error');
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCopy = async (text, label) => {
    const result = await copyToClipboard(text, label);
    showToast(result.message, result.success ? 'success' : 'error');
  };

  // Filter and sort users
  const filteredAndSortedUsers = useCallback(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobile?.includes(searchTerm) ||
        user.id.toString().includes(searchTerm)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      const statusValue = parseInt(filterStatus);
      filtered = filtered.filter(user => user.is_active === statusValue);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, filterStatus, sortConfig]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-chevron-expand sort-icon"></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="bi bi-chevron-up sort-icon active"></i>
      : <i className="bi bi-chevron-down sort-icon active"></i>;
  };

  const displayedUsers = filteredAndSortedUsers();

  return (
    <>
      <div className="user-management">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <h1><i className="bi bi-people-fill"></i> User Management</h1>
            <p className="subtitle">Manage users, plans, and access control</p>
          </div>
          <button className="btn-primary" onClick={handleCreateUser}>
            <i className="bi bi-plus-circle"></i>
            Create User
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="search-filter-bar">
          <div className="search-container">
            <i className="bi bi-search search-icon"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, email, mobile, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>

          <div className="filter-group">
            <label className="filter-label">
              <i className="bi bi-funnel"></i>
              Status:
            </label>
            <select 
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Users</option>
              <option value="0">Active</option>
              <option value="1">Suspended</option>
              <option value="2">Expired</option>
            </select>
          </div>

          <div className="results-count">
            <i className="bi bi-info-circle"></i>
            Showing {displayedUsers.length} of {users.length} users
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="user-table">
              <thead>
                <tr>
                  <th className="sr-no-header">
                    <div className="th-content">
                      <span>Sr. No.</span>
                    </div>
                  </th>
                  <th onClick={() => handleSort('name')} className="sortable">
                    <div className="th-content">
                      <span>User</span>
                      <SortIcon columnKey="name" />
                    </div>
                  </th>
                  <th>
                    <div className="th-content">
                      <span>Contact</span>
                    </div>
                  </th>
                  <th onClick={() => handleSort('plan_days')} className="sortable">
                    <div className="th-content">
                      <span>Plan</span>
                      <SortIcon columnKey="plan_days" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('is_active')} className="sortable">
                    <div className="th-content">
                      <span>Status</span>
                      <SortIcon columnKey="is_active" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('export_count')} className="sortable">
                    <div className="th-content">
                      <span>Exports</span>
                      <SortIcon columnKey="export_count" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('projects_count')} className="sortable">
                    <div className="th-content">
                      <span>Projects</span>
                      <SortIcon columnKey="projects_count" />
                    </div>
                  </th>
                  <th onClick={() => handleSort('created_at')} className="sortable">
                    <div className="th-content">
                      <span>Created</span>
                      <SortIcon columnKey="created_at" />
                    </div>
                  </th>
                  <th className="actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="empty-state">
                      <div className="empty-content">
                        <i className="bi bi-inbox"></i>
                        <h3>No users found</h3>
                        <p>
                          {searchTerm || filterStatus !== 'all' 
                            ? 'Try adjusting your search or filters' 
                            : 'Click "Create User" to add your first user'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedUsers.map((user, index) => {
                    const statusInfo = getStatusInfo(user.is_active);
                    const dateInfo = formatDateTime(user.created_at);

                    return (
                      <tr key={user.id} className="user-row">
                        <td className="sr-no-cell">{index + 1}</td>
                        <td>
                          <div className="user-info">
                            <div className="user-details">
                              <span className="user-name">{user.name}</span>
                              <span className="user-id">ID: {user.id}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="contact-info">
                            <div className="contact-item">
                              <i className="bi bi-envelope"></i>
                              <span>{user.email}</span>
                              <button 
                                className="icon-btn"
                                onClick={() => handleCopy(user.email, 'Email')}
                                title="Copy email"
                              >
                                <i className="bi bi-clipboard"></i>
                              </button>
                            </div>
                            <div className="contact-item">
                              <i className="bi bi-phone"></i>
                              <span>{user.mobile || 'N/A'}</span>
                              {user.mobile && (
                                <button 
                                  className="icon-btn"
                                  onClick={() => handleCopy(user.mobile, 'Mobile')}
                                  title="Copy mobile"
                                >
                                  <i className="bi bi-clipboard"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="plan-info">
                            <span className="plan-duration">
                              <i className="bi bi-clock-history"></i>
                              {user.plan_days} days
                            </span>
                            {user.plan_start_date && user.plan_end_date && (
                              <span className="plan-dates">
                                {new Date(user.plan_start_date).toLocaleDateString('en-GB')} - {new Date(user.plan_end_date).toLocaleDateString('en-GB')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <button
                            className={`status-badge status-${statusInfo.class}`}
                            onClick={() => setStatusUpdateUser(user)}
                            title="Click to change status"
                          >
                            <i className={`bi ${statusInfo.icon}`}></i>
                            {statusInfo.label}
                          </button>
                        </td>
                        <td>
                          {/* Total PDF exports across ALL of this user's projects combined
                              (not per-project) — bumped server-side by /user/record-export
                              every time this user exports the main report PDF. */}
                          <span className="export-count-badge" title="Total PDF exports across all this user's projects">
                            <i className="bi bi-file-earmark-pdf"></i>
                            {user.export_count ?? 0}
                          </span>
                        </td>
                        <td>
                          {/* Live project count for this user (server: withCount('projects'),
                              soft-deleted projects excluded). */}
                          <span className="export-count-badge" title="Number of projects this user has">
                            <i className="bi bi-folder2-open"></i>
                            {user.projects_count ?? 0}
                          </span>
                        </td>
                        <td>
                          <div className="date-info">
                            <span className="date-time">{dateInfo.time}</span>
                            <span className="date-day">{dateInfo.date}</span>
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-btn edit"
                              onClick={() => handleEditUser(user)}
                              title="Edit user"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="action-btn status"
                              onClick={() => setStatusUpdateUser(user)}
                              title="Update status"
                            >
                              <i className="bi bi-shield-check"></i>
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={() => setDeleteConfirmUser(user)}
                              title="Delete user"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modals */}
        {showUserForm && (
          <UserFormModal
            user={editingUser}
            onSubmit={handleUserFormSubmit}
            onClose={() => {
              setShowUserForm(false);
              setEditingUser(null);
            }}
          />
        )}

        {resetPasswordUser && (
          <ResetPasswordModal
            user={resetPasswordUser}
            onSubmit={handleResetPassword}
            onClose={() => setResetPasswordUser(null)}
          />
        )}

        {statusUpdateUser && (
          <StatusUpdateModal
            user={statusUpdateUser}
            onSubmit={handleStatusUpdate}
            onClose={() => setStatusUpdateUser(null)}
          />
        )}

        {deleteConfirmUser && (
          <DeleteConfirmModal
            user={deleteConfirmUser}
            onConfirm={handleDeleteUser}
            onClose={() => setDeleteConfirmUser(null)}
          />
        )}

        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      </div>
    </>
  );
};

export default UserManagement;